import { useModal } from "@/Contexts";
import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { useTerminalsState } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { useConfig } from "@/api/hooks/useConfig";
import { FormColumn, FormFieldType, GridForm } from "@/components/GridForm";
import { Translate, useTrans } from "@/translations";

import { FC, useEffect, useMemo } from "react";
import { UseFormReturn, useFieldArray, useWatch } from "react-hook-form";

import { sortBy } from "lodash";
import { twMerge } from "tailwind-merge";

import {
  CustomerAccessParent,
  CustomerWithAccessParentsAndChain,
} from "../types";

interface CustomerAccessParentsFormSectionProps {
  formHandler: UseFormReturn<CustomerWithAccessParentsAndChain>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  useNumberForHint?: boolean;
  setIsLoading?: (isLoading: boolean) => void;
  isEdit?: boolean;
}

type AccessParents = {
  id: string;
};

export const CustomerAccessParentsFormSection: FC<
  CustomerAccessParentsFormSectionProps
> = ({ formHandler, useNumberForHint = true, setIsLoading, isEdit }) => {
  const { t } = useTrans();
  const {
    isMWM,
    config: { useRedundancyPoints },
  } = useConfig();
  const { ref: modalRef } = useModal();
  const { control, getValues, setValue } = formHandler;
  const { fields: accessParentFields } = useFieldArray({
    control,
    name: "accessParents",
  });
  const { currentTerminal } = useTerminalsState();
  const { data, isLoading, refetch } = trpc.accessParents.getAll.useQuery({
    terminalId: isMWM ? undefined : currentTerminal?.id,
    withRedundancyPoints: useRedundancyPoints,
  });

  useWatch({ control, name: "accessParents" });

  const accessParents = useMemo(() => sortBy(data ?? [], "name"), [data]);

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => setIsLoading?.(isLoading), [isLoading, setIsLoading]);

  const accessParentOptions = useMemo(
    () =>
      accessParents?.map(({ name, id }) => ({
        label: name,
        value: id ?? "",
      })),
    [accessParents],
  );

  const columns: FormColumn[] = useMemo(
    () =>
      [
        {
          label: t("actions:chooseAccessParent"),
          name: "id",
          required: true,
          unique: true,
          uniqueError: t("errors:duplicateStationAdded"),
          fieldType: FormFieldType.SELECT,
          options: accessParentOptions,
          enabled: true,
          placeholder: t("actions:chooseAccessParent"),
          onSelectChange: (value, name) => {
            const index = Number(name.split(".")[1]);
            const accessParent = accessParents?.find(
              (s) => s.id === value.value,
            )!;
            setValue(
              `accessParents.${index}.redundancyPoints`,
              accessParent.redundancyPoints,
            );
          },
        } as FormColumn,
        useRedundancyPoints &&
          ({
            name: "redundancyPoints",
            label: t("entities:redundancyPoints"),
            headerTooltipComponent: (
              <Translate
                i18nKey="hints:redundancyPointDescription"
                components={[
                  // Anchor content is added by the translation
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a
                    href="mailto:support@carrot.tech"
                    className="text-white underline"
                  />,
                ]}
              />
            ),
            fieldType: FormFieldType.READ_ONLY,
            readOnlyContent: (rowIndex) => {
              const accessParent = getValues("accessParents")[rowIndex];
              return <ReadOnlyRedundancyPoints accessParent={accessParent} />;
            },
          } as FormColumn),
      ].filter(Boolean) as FormColumn[],
    [
      t,
      accessParentOptions,
      useRedundancyPoints,
      accessParents,
      setValue,
      getValues,
    ],
  );

  if (isLoading) {
    return (
      <div className="flex h-[150px] min-w-[200px] flex-col items-center justify-center gap-4">
        <LoadingIndicator />
        {t("systemMessages:loadingStations") + "..."}
      </div>
    );
  }

  return (
    <div className="mb-8 md:ml-3">
      <div className={twMerge(isEdit && `bg-grey1 mb-4 p-5`)}>
        <h2 className="font-dm-sans mb-2 text-base font-medium">{`${
          useNumberForHint ? "2. " : ""
        }${t("hints:tenantRoomAccess")}`}</h2>
        <p
          className={twMerge(
            "font-dm-sans text-sm font-normal text-black",
            !isEdit && "mb-4",
          )}
        >
          <Translate
            i18nKey={"hints:tenantRoomAccessDetails"}
            values={{
              link: t("sidebarLabels:manageInfrastructure"),
            }}
            components={[
              <a
                href="/infrastructure/manage"
                style={{ textDecoration: "underline" }}
                target="_blank"
                rel="noreferrer"
              >
                ${t("sidebarLabels:manageInfrastructure")}
              </a>,
            ]}
          />
        </p>
      </div>
      <GridForm<AccessParents[]>
        formHandler={formHandler}
        columns={columns}
        enableDeleteColumn
        allowDeletingLastRow={false}
        enableAddRowButton={
          accessParentFields.length < (accessParents?.length ?? 0)
        }
        selectInputMenuPortalTarget={modalRef.current}
        withColSpacing
      />
    </div>
  );
};

export const ReadOnlyRedundancyPoints: FC<{
  accessParent?: CustomerAccessParent;
}> = ({ accessParent }) => {
  const { t } = useTrans();
  const { redundancyPoints = [] } = accessParent ?? {};

  if (!accessParent?.id) {
    return null;
  }

  return (
    <ul className="list-disc text-sm">
      {!redundancyPoints?.length ? (
        <li>{t("hints:noRedundancyPoints")}</li>
      ) : (
        redundancyPoints?.map(({ id, name }) => <li key={id}>{name}</li>)
      )}
    </ul>
  );
};
